import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "store";

import { Order, Refill, TransactionMode, TransactionState } from "types";

type TransactionSlice = {
	order: Order | null;
	refill: Refill | null;
	currentOrderStep: number;
};

const initialState: TransactionSlice = {
	order: null,
	refill: null,
	currentOrderStep: 1,
};

export const transactionSlice = createSlice({
	name: "transaction",
	initialState,
	reducers: {
		setOrder: (state, { payload }: PayloadAction<Order | null>) => {
			state.order = payload;
		},
		setRefill: (state, { payload }: PayloadAction<Refill | null>) => {
			state.refill = payload;
		},
		updateCurrentOrderStep: (state, action: PayloadAction<number>) => {
			state.currentOrderStep = action.payload;
		},
		finishOrderState: (state) => {
			state.order = null;
			state.refill = null;
			state.currentOrderStep = 1;
		},
	},
});

export const { finishOrderState, setOrder, setRefill, updateCurrentOrderStep } =
	transactionSlice.actions;
export const selectOrder = (state: RootState) => state.transaction?.order || null;
export const selectRefill = (state: RootState) => state.transaction?.refill || null;
export const selectUserPreferences = (state: RootState) =>
	state.transaction?.order?.user?.preferences || null;
export const selectOrderState = (state: RootState) => {
	return state.transaction.order?.state || null;
};
export const selectHasActiveOrder = (state: RootState) => {
	return state.transaction?.order !== null;
};
export const selectRefillMode = (state: RootState): TransactionMode | null => {
	const refill = state.transaction.refill;
	if (!refill || refill.state === TransactionState.Finished) return null;
	return refill.mode || null;
};
export const selectCurrentOrderStep = (state: RootState) => {
	return state.transaction.currentOrderStep;
};
export const selectOrderDishes = createSelector(
	selectOrder,
	(order) => order?.inventory?.removed || [],
);
export const selectIsEmptyOrder = (state: RootState) => {
	const order = state.transaction.order;
	if (!order) {
		return false;
	}
	const orderHasEmptyInventory = order.inventory?.removed?.length === 0 || !order.inventory;
	return orderHasEmptyInventory && order.state === TransactionState.Finished;
};

// TODO: add fetching logic for when there are unknown dish types in the order
export const selectOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectOrder],
	(fridgeDishes, order) => {
		if (fridgeDishes) {
			if (!order?.inventory?.removed) {
				return [];
			}
			const orderDishes = order.inventory.removed;
			const fullPriceDishes = orderDishes.filter((dish) => dish.price?.default == null);
			const orderDishTypeIds = new Set(fullPriceDishes.map((dish) => dish?.type?.id));
			const orderDishesById = new Map(orderDishes.map((dish) => [dish.type?.id, dish]));
			return fridgeDishes
				?.filter((dishType) => orderDishTypeIds.has(dishType.id))
				.map((dishType) => {
					const orderDish = orderDishesById.get(dishType.id);
					return {
						...dishType,
						...(orderDish?.price && {
							price: { ...dishType.price, ...orderDish.price },
						}),
					};
				});
		}
		return [];
	},
);

export const selectDiscountedOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectOrder],
	(fridgeDishes, order) => {
		if (fridgeDishes) {
			if (!order?.inventory?.removed) {
				return [];
			}
			const orderDishes = order.inventory.removed;
			const discountedDishes = orderDishes.filter((dish) => dish.price?.default != null);
			const discountedDishTypeIds = new Set(discountedDishes.map((dish) => dish?.type?.id));
			const discountedDishesById = new Map(
				discountedDishes.map((dish) => [dish.type?.id, dish]),
			);

			return fridgeDishes
				?.filter((dishType) => discountedDishTypeIds.has(dishType.id))
				.map((dishType) => {
					const discountedDishPrice = discountedDishesById.get(dishType.id)?.price;
					return {
						...dishType,
						...(discountedDishPrice && {
							price: { ...discountedDishPrice },
						}),
					};
				});
		}
		return [];
	},
);

export const selectOrderSummary = createSelector(
	selectOrder,
	selectOrderDishTypes,
	selectDiscountedOrderDishTypes,
	(order, orderDishTypes, discountedDishTypes) => {
		if (!order) {
			return null;
		}

		const orderDishes = order.inventory?.removed || [];
		const orderDishTypeIds = new Set(
			orderDishes.filter((dish) => dish.type?.id != null).map((dish) => dish?.type?.id),
		);

		// Count full-priced dishes per dish type
		const fullPricedDishCounts = orderDishes
			.filter((dish) => dish.price?.default == null)
			.reduce((acc, dish) => {
				const dishTypeId = dish.type?.id;
				if (dishTypeId) {
					acc[dishTypeId] = (acc[dishTypeId] || 0) + 1;
				}
				return acc;
			}, {} as Record<string, number>);

		// Count discounted dishes per dish type
		const discountedDishCounts = orderDishes
			.filter((dish) => dish.price?.default != null)
			.reduce((acc, dish) => {
				const dishTypeId = dish.type?.id;
				if (dishTypeId) {
					acc[dishTypeId] = (acc[dishTypeId] || 0) + 1;
				}
				return acc;
			}, {} as Record<string, number>);

		return {
			orderDishTypesCount: orderDishTypeIds.size ?? 0,
			orderDishTypes: orderDishTypes || [],
			discountedDishTypes: discountedDishTypes || [],
			orderPrice: order?.price,
			fullPricedDishCounts: fullPricedDishCounts,
			discountedDishCounts: discountedDishCounts,
		};
	},
);

export const selectOrderPaymentMethod = createSelector(
	selectOrder,
	(order) => order?.payment?.method?.type || null,
);

export default transactionSlice.reducer;
